import {filter} from 'rxjs';

import {Drawable} from './actor';
import Decimal from './break_infinity';
import {Shape} from './interfaces/shape';
import {p5, ri} from './runner-idle';
import {Vector} from './Vector';

export class Region implements Drawable, Shape {
  // #region Properties (6)

  public toString(): string {
    return `${this.topLeft.x} < x < ${this.topLeft.x.plus(
      this.width
    )}, ${this.topLeft.y.minus(this.height)} < y < ${this.topLeft.y}`;
  }
  leftBound: Decimal = Decimal.POSITIVE_INFINITY;
  rightBound: Decimal = Decimal.NEGATIVE_INFINITY;
  topBound: Decimal = Decimal.NEGATIVE_INFINITY;
  bottomBound: Decimal = Decimal.POSITIVE_INFINITY;

  public fillColor: [number, number, number, number?];
  public height: Decimal;
  public name: string;
  public strokeColor: [number, number, number, number?];
  #topLeft!: Vector;
  public get topLeft(): Vector {
    return this.#topLeft;
  }
  public set topLeft(value: Vector) {
    this.#topLeft = value;
    this.#bottomLeft = value.minus(new Vector(0, this.height));
  }
  #bottomLeft!: Vector;
  public get bottomLeft(): Vector {
    return this.#bottomLeft;
  }
  public set bottomLeft(value: Vector) {
    this.#bottomLeft = value;
    this.#topLeft = value.plus(new Vector(0, this.height));
  }
  public width: Decimal;
  rotation: number;
  /** The amount the tags of this Region extend beyond its borders. */
  tagRange: Decimal = new Decimal(0);

  // #endregion Properties (6)

  // #region Constructors (1)

  contains(point: Vector) {
    const rotatedPoint = point.rotated(this.rotation, this.center);
    return (
      rotatedPoint.x.gte(this.topLeft.x) &&
      rotatedPoint.x.lte(this.topLeft.x.plus(this.width)) &&
      rotatedPoint.y.gte(this.topLeft.y.minus(this.height)) &&
      rotatedPoint.y.lte(this.topLeft.y)
    );
  }

  /** Is this point in the axially-aligned bounding box for this Region? */
  inAABB(point: Vector): boolean {
    return (
      point.x.gte(this.leftBound) &&
      point.x.lte(this.rightBound) &&
      point.y.gte(this.bottomBound) &&
      point.y.lte(this.topBound)
    );
  }

  /** Is this point outside the axially-aligned bounding box for this Region? */
  outAABB(point: Vector): boolean {
    return (
      point.x.lt(this.leftBound) ||
      point.x.gt(this.rightBound) ||
      point.y.lt(this.bottomBound) ||
      point.y.gt(this.topBound)
    );
  }

  hasTagsAt(point: Vector): boolean {
    if (this.outAABB(point)) return false;
    return (
      this.contains(point) ||
      this.nearestPointTo(point).distance(point).lte(this.tagRange)
    );
  }
  constructor({
    name = '',
    topLeft = undefined,
    bottomLeft = undefined,
    width = new Decimal(10),
    height = new Decimal(10),
    fillColor = [255, 255, 255],
    strokeColor = [0, 0, 0, 0],
    direction: rotation = 0,
    tagRange = new Decimal(0),
    z = new Decimal(0),
  }: {
    name?: string;
    topLeft?: Vector;
    bottomLeft?: Vector;
    width?: Decimal;
    height?: Decimal;
    fillColor?: [number, number, number, number?];
    strokeColor?: [number, number, number, number?];
    direction?: number;
    tagRange?: Decimal;
  }) {
    this.name = name;
    this.width = width;
    this.height = height;
    if (topLeft) {
      this.topLeft = topLeft;
    } else if (bottomLeft) {
      this.bottomLeft = bottomLeft;
    }
    this.fillColor = fillColor;
    this.strokeColor = strokeColor;
    this.rotation = rotation;
    this.tagRange = tagRange;
    ri.position$
      .pipe(filter(this.contains.bind(this)))
      .subscribe(this.interact.bind(this));
    if (this.rotation == 0) {
      this.leftBound = this.topLeft.x;
      this.rightBound = this.topLeft.x.plus(this.width);
      this.topBound = this.topLeft.y;
      this.bottomBound = this.bottomLeft.y;
    } else {
      const rotatedTopLeft = this.topLeft.rotated(this.rotation, this.center);
      const rotatedTopRight = this.topLeft
        .plus(new Vector(this.width, 0))
        .rotated(this.rotation, this.center);
      const rotatedBottomLeft = this.bottomLeft.rotated(
        this.rotation,
        this.center
      );
      const rotatedBottomRight = this.bottomLeft
        .plus(new Vector(this.width, 0))
        .rotated(this.rotation, this.center);
      this.leftBound = rotatedTopLeft.x
        .min(rotatedTopRight.x)
        .min(rotatedBottomLeft.x)
        .min(rotatedBottomRight.x);
      this.rightBound = rotatedTopLeft.x
        .max(rotatedTopRight.x)
        .max(rotatedBottomLeft.x)
        .max(rotatedBottomRight.x);
      this.topBound = rotatedTopLeft.y
        .max(rotatedTopRight.y)
        .max(rotatedBottomLeft.y)
        .max(rotatedBottomRight.y);
      this.bottomBound = rotatedTopLeft.y
        .min(rotatedTopRight.y)
        .min(rotatedBottomLeft.y)
        .min(rotatedBottomRight.y);
    }
  }
  distance(point: Vector): Decimal {
    return this.nearestPointTo(point).distance(point);
  }
  nearestPointTo(point: Vector): Vector {
    const rotatedPoint = point.rotated(this.rotation, this.center);
    if (this.contains(rotatedPoint)) return rotatedPoint;
    const x = rotatedPoint.x.clamp(
      this.bottomLeft.x,
      this.bottomLeft.x.plus(this.width)
    );
    const y = rotatedPoint.y.clamp(
      this.bottomLeft.y,
      this.bottomLeft.y.plus(this.height)
    );
    const dl = x.minus(this.bottomLeft.x).abs();
    const dr = x.minus(this.bottomLeft.x.plus(this.width)).abs();
    const db = y.minus(this.bottomLeft.y).abs();
    const dt = y.minus(this.bottomLeft.y.plus(this.height)).abs();
    const min = dl.min(dr).min(db).min(dt);
    let result;
    switch (min) {
      case dl:
        result = new Vector(this.bottomLeft.x, y);
        break;
      case dr:
        result = new Vector(this.bottomLeft.x.plus(this.width), y);
        break;
      case db:
        result = new Vector(x, this.bottomLeft.y);
        break;
      case dt:
        result = new Vector(x, this.bottomLeft.y.plus(this.height));
        break;
      default:
        throw new Error('Unreachable Code');
    }
    return result.rotated(-this.rotation, this.center);
  }

  // #endregion Constructors (1)

  get center() {
    return new Vector(
      this.topLeft.x.plus(this.width.div(2)),
      this.topLeft.y.minus(this.height.div(2))
    );
  }

  // #region Public Methods (1)

  public draw(p: typeof p5, zoomScale: Decimal): void {
    const adjustedX = this.topLeft.x.div(zoomScale);
    const adjustedY = this.topLeft.y.div(zoomScale);
    const adjustedW = this.width.div(zoomScale);
    const adjustedH = this.height.div(zoomScale);
    if (adjustedW.lt(0.01) || adjustedH.lt(0.01)) {
      return;
    }
    if (ri.settings.map.showRegionNames) {
      p.push();
      p.fill(0);
      p.stroke(0);
      p.textSize(16);
      p.scale(1, -1);
      p.rotate(this.rotation);
      p.text(this.name, adjustedX.toNumber(), adjustedY.negate().toNumber());
      p.pop();
    }
    p.push();
    p.fill(...this.fillColor);
    p.stroke(...this.strokeColor);
    p.translate(
      // this.width.toNumber() / zoomScale.toNumber() / 2,
      // this.height.toNumber() / zoomScale.toNumber() / 2,
      // 0
      adjustedX.plus(adjustedW.div(2)).toNumber(),
      adjustedY.minus(adjustedH.div(2)).toNumber()
    );
    p.rotate(this.rotation);
    p.rect(0, 0, adjustedW.toNumber(), adjustedH.toNumber());
    p.pop();
  }

  /** What happens when the player enters this region? */
  public interact() {
    // console.log(`interact player ${this.name}`);
  }

  // #endregion Public Methods (1)
}

export function createLine(
  start: Vector,
  end: Vector,
  {
    thickness = new Decimal(0.1),
    fillColor = [0, 0, 0, 0],
    strokeColor = fillColor,
    name = '',
    tagRange = new Decimal(0),
  }: {
    thickness?: Decimal;
    fillColor?: [number, number, number, number?];
    strokeColor?: [number, number, number, number?];
    name?: string;
    tagRange?: Decimal;
  } = {}
): Region {
  // Never run directly right-to-left, it messes with distance algorithms.
  if (start.y.eq(end.y) && start.x.gt(end.x)) {
    [start, end] = [end, start];
  }
  const offset = end.minus(start);
  const length: Decimal = offset.magnitude;
  const direction: number = offset.direction.toNumber();
  return new Region({
    name,
    topLeft: new Vector(
      start.plus(offset.div(2)).x.minus(length.div(2)),
      start.plus(offset.div(2)).y.plus(thickness.div(2))
    ),
    width: length,
    height: thickness,
    direction,
    fillColor,
    strokeColor,
    tagRange,
  });
}
